import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {DataLoggerDefaults, Device, Register} from "../../model/device.model";
import {ModbusService} from "../../modbusrtu/modbus.service";
import {MeterRegisters} from "../meter-overview/meter-overview.component";
import {DeviceAttribute} from "../../model/device-attribute.model";
import {MatDialog} from "@angular/material/dialog";
import {MeasureDialogComponent} from "./measure-dialog/measure-dialog.component";
import {UtilService} from "../../shared/util.service";

@Component({
    selector: 'app-device-measurement',
    templateUrl: './device-measurement.component.html',
    standalone: false
})
export class DeviceMeasurementComponent implements OnInit, OnChanges {

  @Input() device: Device;
  @Input() isAdmin: boolean = false;
  @Input() deviceData: any = {};
  @Output() deviceDataChange = new EventEmitter<any>();

  measures: any[] = [];
  modbusRTUs: any[] = [];

  status3f: any = null;
  writeList : any[] = [];

  measureRegisters = [];
  measureRegisterStart = 108;

  loggingOnGateway = false;

  constructor(
    private modbusService: ModbusService,
    private dialog: MatDialog,
    private util: UtilService
  ) { }

  ngOnInit(): void {
    this.extractSavedRegisters(this.device.registers)
    this.fetchModbusRTUMeasures();
    this.extractMeasureResponse({});
    this.loggingOnGateway = this.deviceData && this.deviceData.port && this.deviceData.port > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.deviceData) {
      this.deviceData = changes.deviceData.currentValue;
    }
  }

  extractSavedRegisters(registers) {
    const index = this.device.getIndex();
    if (+index > -1 && registers) {
      this.deviceData.port = registers['port'];
      this.deviceData.push_period = registers[Register.push_period];
      this.deviceData.logging_period = registers[(+index * 100) + MeterRegisters.readfreq];
      this.deviceDataChange.emit(this.deviceData);
    }
  }

  onClickAdhocBtn() {
    if (!this.device.parent) {
      return;
    }
    const ip = this.device.parent.getIP();
    if (ip) {
      this.modbusService.write(ip, Register.ad_hoc, 1, this.device.parent.id).subscribe((resp:any) => {
        if (resp.error) {
          return this.modbusService.notiService.open(resp.error);
        }
        this.modbusService.notiService.open("Successful adhoc register writing");
        console.log("write res", resp);
      })
    }
  }

  onQueryClick(measure) {
    if (!this.device.parent) {
      return;
    }
    if (!measure.address) {
      return this.modbusService.notiService.open("Missing measure address (register)");
    }
    const data = {
      deviceid: this.device.id,
      ip: this.device.parent.getIP(),
      port: this.device.getPort(),
      device: this.device.getAddress(),
      register: measure.address,
      type: 'measure'
    }

    this.modbusService.query(data).subscribe((resp:any) => {
      if (resp.error) {
        return this.modbusService.notiService.open(resp.error);
      }
      measure.value = resp[measure.address] ? resp[measure.address] : null;
    })
  }

  onClick3fBtn() {
    if (!this.device.parent) {
      return;
    }
    const data = {
      deviceid: this.device.id,
      ip: this.device.parent.getIP(),
      port: this.device.getPort(),
      device: this.device.getAddress(),
    }

    this.modbusService.getStatus3f(data).subscribe((resp:any) => {
      if (resp.errors && resp.errors.length) {
        return this.modbusService.notiService.open("Something went wrong during status3f reading. Check console logs");
      }
      if (resp.list && resp.list.length) {
        this.status3f = resp.list;
      }
    })
  }

  sendQuery(info) {
    this.modbusService.query(info).subscribe((resp:any) => {
      if (resp.error) {
        return this.modbusService.notiService.open(resp.error);
      }
    })
  }

  getCachedMeasureStatus() {
    const index = this.device.getIndex();
    if (+index > -1 && this.device.registers) {
      const neededRegisters = [];
      const registerStart = (+index * 100) + MeterRegisters.firstmeasure;
      for (let i = 0; i < 18; i++) {
        let reg = registerStart + i;
        neededRegisters[reg] = this.device.registers[reg];
      }
      this.extractMeasureResponse(neededRegisters);
    }
  }

  getMeasureStatus() {
    if (!this.device.parent) {
      return;
    }
    const data = {
      deviceid: this.device.id,
      ip: this.device.parent.getIP(),
      index: this.device.getIndex(),
      port: DataLoggerDefaults.port,
      device: DataLoggerDefaults.device,
      type: 'meter_measures'
    }
    this.modbusService.query(data).subscribe((resp:any) => {
      if (resp.error) {
        return this.modbusService.notiService.open(resp.error);
      }
      this.extractMeasureResponse(resp);
    })
  }

  purgeMeasures() {
    this.resetMeasureLoggingState();
    this.measureRegisters = [];
    const data = {
      ip: this.device.parent.getIP(),
      index: this.device.getIndex(),
      type: 'purge',
      address: 1,
      length: 1,
      parent: this.device.parent.id
    }
    this.modbusService.writeMeasure(data).subscribe((resp:any) => {
      if (!resp.success) {
        this.modbusService.notiService.open("Something went wrong, check console log.");
      } else {
        this.modbusService.notiService.open("Successful writing");
      }
    })
  }

  confirmPurge() {
    this.modbusService.confirmAction(`Do you want to reset the first 5 measure? (This action will zero out the first 5*4 registers.)`).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.purgeMeasures()
      }
    })
  }

  private resetMeasureLoggingState() {
    this.measures = [];
  }

  private checkIfMeasureActive(measureRegisters, checkOnly = false) {
    if (!measureRegisters || !measureRegisters.length) {
      return null;
    }
    const register = measureRegisters[0].value;
    const type = measureRegisters[1].value;

    if (+register === 0 && +type === 0) {
      // skip empty registers with empty types
      return null;
    }
    let rtu = this.modbusRTUs.find((rtu:any) => +register == +rtu.address);
    if (!rtu) {
      return null;
    }

    rtu.loggingEnabled = type != 255;
    rtu.registers = measureRegisters;

    if (checkOnly) {
      return null;
    }
    this.measures.push(rtu);
  }

  onClickMeasure(measure?) {
    const dialogRef = this.dialog.open(MeasureDialogComponent, {
      data: {
        measure: measure,
        ip: this.device.parent.getIP(),
        index: this.device.getIndex(),
        modbusRTUs: this.modbusRTUs,
        parent: this.device.parent.id,
        device: this.device.id,
      },
      minWidth: '300px',
      maxWidth: '500px'
    });

    dialogRef.afterClosed().subscribe( (result: any) => {
      if (result) {
        if (measure) {
          let idx = this.measures.findIndex((m:any) => m.id == measure.id && m.address == measure.address);
          this.measures[idx] = result;
        } else {
          result.loggingEnabled = true;
          this.measures.push(result)
        }
        this.resetMosbusRTULoggingStatuses();
      }
    });
  }

  resetMosbusRTULoggingStatuses() {
    this.modbusRTUs = this.modbusRTUs.map((rtu: any) => {
      rtu.loggingEnabled = false;
      return rtu;
    })
    this.measures.forEach((measure: any) => {
      if (measure.registers) {
        this.checkIfMeasureActive(measure.registers, true);
      } else {
        console.log("ERROR: missing measure registers", measure);
      }
    })
  }

  /*
  * extract list of registers as measure groups (4 register 1 group)
  */
  private extractMeasureResponse(resp) {
    if (!resp) {
      return;
    }
    this.resetMeasureLoggingState();
    const list = Object.entries(resp);
    let count = 0;
    this.measureRegisters = [];
    let obj: any = {};
    let name = '';
    let id = '';
    let measure = [];
    for(let i = 0; i < list.length; i++) {
      obj = {};
      if (count >= 4) {
        count = 0;
        this.checkIfMeasureActive(measure);
        this.measureRegisters.push(measure);
        measure = [];
      }
      switch (count) {
        case 0:
          id = 'start';
          name = 'Start address';
          break;
        case 1:
          id = 'type';
          name = 'Type';
          break;
        case 2:
          id = 'length';
          name = 'Length';
          break;
        case 3:
          id = 'neighbours';
          name = 'Neighbours';
          break;
        default:
          id = 'missing';
          name = 'missing';
      }
      obj.id = id;
      obj.name = name;
      obj.value = list[i][1];
      obj.register = list[i][0];
      measure.push(obj);
      count++;
    }
    // add last measure to the list as well
    if (count >= 4) {
      this.checkIfMeasureActive(measure);
      this.measureRegisters.push(measure);
    }
  }

  // Get port and push periods (from datalogger)
  getLoggerStatus() {
    if (!this.device.parent) {
      return;
    }
    const data = {
      deviceid: this.device.id,
      ip: this.device.parent.getIP(),
      port: DataLoggerDefaults.port,
      device: DataLoggerDefaults.device,
      type: 'loggerstate'
    }
    this.modbusService.query(data).subscribe((resp:any) => {
      if (resp.error) {
        return this.modbusService.notiService.open(resp.error);
      }
      if (resp[Register.port]) {
        this.deviceData.port = resp[Register.port];
      }
      if (resp[Register.push_period]) {
        this.deviceData.push_period = resp[Register.push_period];
      }
      if (resp['freq']) {
        this.deviceData.logging_period = resp['freq'];
      }
      this.deviceDataChange.emit(this.deviceData);
    })
  }

  fetchModbusRTUMeasures() {
    if (this.device && this.device.type) {
      this.modbusService.getModbusRTUMeasures(this.device.type.id).subscribe((resp: any) => {
        if (resp.error) {
          return this.modbusService.notiService.open(resp.error);
        }
        if (resp.length) {
          this.modbusRTUs = resp.filter(r => {
            if (r.measure) {
              return r;
            }
          })
          this.getCachedMeasureStatus();
        }
      })
    }
  }

  handleWriteRegisters(measure, type) {
    const data = {
      ip: this.device.parent.getIP(),
      address: measure.address,
      oldaddress: measure.address,
      length: measure.length,
      typemodbus: measure.loggingEnabled ? measure.datatypemodbus : 255,
      index: this.device.getIndex(),
      type: type,
      parent: this.device.parent.id
    }
    this.modbusService.writeMeasure(data).subscribe((resp:any) => {
      if (!resp.success) {
        this.modbusService.notiService.open("Something went wrong, check console log.");
      } else {
        this.modbusService.notiService.open("Successful writing");
        if (resp.saved) {
          measure.registers = this.util.convertRawMeasureRegistersToTableData(resp.saved);
        }
        if (type == 'delete') {
          let idx = this.measures.findIndex((m:any) => m.id == measure.id && m.address == measure.address);
          this.measures.splice(idx,1);
        }
      }
    })
  }

  onLoggingChange(measure) {
    const index = +this.device.getIndex();
    if (!index) {
      return this.modbusService.notiService.open('Missing device index attribute');
    }
    this.modbusService.confirmAction(`Do you want to write measure register?`).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.measureRegisters = [];
        this.handleWriteRegisters(measure, 'edit');
      } else {
        measure.loggingEnabled = !measure.loggingEnabled;
      }
    })
  }

  onClickMeasureDelete(measure) {
    const index = +this.device.getIndex();
    if (!index) {
      return this.modbusService.notiService.open('Missing device index attribute');
    }
    this.modbusService.confirmAction(`Do you want to remove these measure register values?`).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.measureRegisters = [];
        this.handleWriteRegisters(measure, 'delete');
      } else {
        measure.loggingEnabled = !measure.loggingEnabled;
      }
    })
  }

  onLoggingPeriodChange() {
    if (this.deviceData.logging_period > 0) {
      this.deviceData.logging_period = 0;
      this.writeLoggingRegister();
    } else {
      const freq = +this.device.getReadFreq();
      if (freq > 0) {
        this.deviceData.logging_period = freq;
        this.writeLoggingRegister();
      }
    }
  }

  writeLoggingRegister() {
    if (!this.device.parent) {
      return;
    }
    const index = this.device.getIndex();
    if (index === null) {
      return this.modbusService.notiService.open("Missing device index for register writing.");
    }
    const ip = this.device.parent.getIP();
    const data = {
      register: (+index * 100) + MeterRegisters.readfreq,
      value: this.deviceData.logging_period,
      id: 'meter_register'
    }

    this.modbusService.writeMultiple({deviceid: this.device.parent.id, ip: ip, list: [data]}).subscribe((resp:any) => {
      if (!resp.success) {
        this.modbusService.notiService.open("Something went wrong, check console log.");
        console.log(resp);
      } else {
        this.modbusService.notiService.open("Successful writing");
      }
    })
  }

}
